import { createSlice } from "@reduxjs/toolkit";
// api
import { initExportShipmentAPI, initExportShipmentBillingDetailAPI, voidLabelAPI, downloadLabelAPI, getInsuranceRateAPI } from "src/api";
// redux
import { recursiveCheckTaskById } from "src/redux/slices/task";
import { updateDisplayTaskPopover } from "src/redux/slices/other";
// sections
import { getRequestBody } from "src/sections/shipment/utility/format-data";
// utils
import apiSlice from "src/utils/api-slice";
import { openFile } from "src/utils/handle-task";
import { trimObject } from "src/utils/format-object";
import { getPrevious30DaysLocalMidnightDate, getTodayLocalEndOfDayDate } from "src/utils/get-date";

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    error: null,
    shipmentLabelFilter: {
        shipment_status: "all",
        env: "all",
        start_day: getPrevious30DaysLocalMidnightDate(),
        end_day: getTodayLocalEndOfDayDate(),
        keyword: "",
        search_category: 'tracking_no',
        page: 1,
        page_size: 100,
        total: 0
    }
};

const slice = createSlice({
    name: "shipment",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },

        stopLoading(state) {
            state.isLoading = false;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        updateShipmentListFilter(state, action) {
            state.shipmentLabelFilter = {
                ...state.shipmentLabelFilter,
                ...action.payload
            };
        },

        resetShipmentListFilter(state) {
            state.shipmentLabelFilter = {
                ...state.shipmentLabelFilter,
                //
                shipment_status: "all",
                env: 'all',
                keyword: "",
                page: 1,
                start_day: getPrevious30DaysLocalMidnightDate(),
                end_day: getTodayLocalEndOfDayDate(),
                search_category: 'tracking_no',
            };
        },
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    updateShipmentListFilter,
    resetShipmentListFilter,
    //
    startLoading,
    stopLoading,
    //
    clearError
} = slice.actions;

// ----------------------------------------------------------------------

export function getInsuranceValue(value, serviceLevel, signal) {
    return async (dispatch) => {
        try {
            const { status, data } = await getInsuranceRateAPI({
                declared_value: value,
                service_level: serviceLevel
            })(signal);  // 传递 AbortSignal

            return status === 200 ? data?.data : 0;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return 0;
        }
    };
}

// ----------------------------------------------------------------------

export function voidLabel(id) {
    return async (dispatch) => {
        try {
            const { status } = await voidLabelAPI(id);

            if (status === 200) {
                dispatch(apiSlice.util.invalidateTags([ "ShipmentList", "PickupUSPSAvailableList" ]));
            }

            return status === 200;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        }
    };
}

// ----------------------------------------------------------------------

export function downloadShipmentLabel(ids, size) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const request = {
                shipment_nos: ids,
                size,
                position: ""
            };

            const { status, data } = await downloadLabelAPI({ ...request });

            if (status === 200) {
                openFile(data);
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function getShipmentExportInit(snackbar) {
    return async (dispatch, getState) => {
        try {
            dispatch(slice.actions.startLoading());

            const _filter = getState().shipment.shipmentLabelFilter;
            const _sidebarFilter = getState().shipment.shipmentSidebarFilter;

            const request = {
                ...trimObject(getRequestBody({ ..._filter, ..._sidebarFilter }))([ "page", "page_size", "total" ]),
                //
                time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone
            };

            const { status, data } = await initExportShipmentAPI({ ...request });

            if (status === 200 && data?.code === "ok") {
                dispatch(recursiveCheckTaskById(data?.data, 0, snackbar));
                //
                setTimeout(() => dispatch(updateDisplayTaskPopover(true)), 750);
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

// XOA-976: billing detail export, scoped to company 42038 only.
export function getShipmentBillingExportInit(filter, snackbar) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await initExportShipmentBillingDetailAPI({ ...filter });

            if (status === 200 && data?.code === "ok") {
                dispatch(recursiveCheckTaskById(data?.data, 0, snackbar));
                //
                setTimeout(() => dispatch(updateDisplayTaskPopover(true)), 750);
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}
